import * as React from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Autocomplete,
  Box,
  Collapse,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useGetList, useTranslate } from 'react-admin';
import { FilterOption, TaskTemplateField, XgacFilter } from './types';
import {
  buildCompoundFilterKey,
  embeddedTimestampToDisplaySeconds,
  embeddedTimestampToStoredMilliseconds,
  flattenXgacFilter,
  getComparatorsForFamily,
  getFilterFamily,
  getSelectorFilterOptions,
  isNewFilterKey,
  isResultOnlyFilterKey,
  isValidFilterKey,
  mergeFilterOptions,
  NEW_FILTER_KEY_PREFIX,
  parseCompoundFilterKey,
  resolveFilterKey,
  splitXgacFilter,
} from './xgacFilterDefinitions';
import {
  formatOperatorValue,
  getComparatorOptions,
  getDefaultResultFilterValue,
  parseOperatorValue,
  ResultOperator,
} from './xgacFilterOperators';
import { NumericInputField, NumericTextField } from './NumericInputField';

const RESULT_FILTER_PATTERN = /^(==|!=|<=?|>=?)(true|false|[0-9]+|%alarmAssetId%)$/;

const FILTER_ROW_SX = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr auto',
  gap: 1,
  alignItems: 'start',
  width: '100%',
  mb: 1,
} as const;

const FILTER_COLUMN_SX = {
  minWidth: 0,
  width: '100%',
  '& .MuiFormControl-root': { width: '100%' },
  '& .MuiAutocomplete-root': { width: '100%' },
} as const;

const ANY_OWN_GROUPS_VALUE = '%anyOwnGroups%';
const ANY_OWN_GROUPS_EQUALS = `==${ANY_OWN_GROUPS_VALUE}`;
const ANY_OWN_GROUPS_NOT_EQUALS = `!=${ANY_OWN_GROUPS_VALUE}`;

const useTranslateLabel = () => {

  const translate = useTranslate();
  return (key: string, fallback: string) => {

    const value = translate(key, { _: fallback });
    return typeof value === 'string' && value.trim().length > 0 ? value : fallback;

  };

};

type XgacFilterEditorProps = {
  value: XgacFilter;
  onChange: (value: XgacFilter) => void;
  filterField: TaskTemplateField | null;
  disabled?: boolean;
};

const getFilterValueInputType = (key: string): 'groups' | 'insideZones' | 'embeddedTimestamp' | 'id' | 'text' => {

  if (key.startsWith('groups')) return 'groups';
  if (key.startsWith('insideZones')) return 'insideZones';
  if (key.startsWith('embeddedTimestamp')) return 'embeddedTimestamp';
  if (key.startsWith('id')) return 'id';
  return 'text';

};

const ReferenceValueInput = ({
  reference,
  value,
  onChange,
  disabled,
  label,
  extraOptions = [],
}: {
  reference: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label: string;
  extraOptions?: { id: string; name?: string }[];
}) => {

  const { data: options = [], isLoading } = useGetList<{ id: string; name?: string }>(reference, {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  });

  const allOptions = [...extraOptions, ...options];
  const selected = allOptions.find((option) => option.id === value)
    || (value ? { id: value, name: value } : null);
  const autocompleteOptions = selected && !allOptions.some((option) => option.id === selected.id)
    ? [selected, ...allOptions]
    : allOptions;

  return (
    <Autocomplete<{ id: string; name?: string }>
      options={autocompleteOptions}
      getOptionLabel={(option) => option.name || option.id}
      value={selected}
      disabled={disabled || isLoading}
      onChange={(_, newValue) => onChange(newValue?.id || '')}
      renderInput={(params) => <TextField {...params} label={label} size="small" fullWidth />}
      fullWidth
      isOptionEqualToValue={(option, val) => option.id === val.id}
    />
  );

};

const ASSET_ID_NOT_OWN = '!=%alarmAssetId%';
const ASSET_ID_OWN = '==%alarmAssetId%';
const CONTACTED_NOT_BEFORE = '!=true';
const AVAILABLE_YES = '==true';
const AVAILABLE_NO = '!=true';

const ContactedFilterValueInput = ({
  label,
}: {
  label: string;
}) => {

  const translateLabel = useTranslateLabel();

  return (
    <TextField
      size="small"
      fullWidth
      value={translateLabel('resources.process-templates.text.contacted_not_before', 'Not contacted before')}
      label={label}
      disabled
    />
  );

};

const AssetIdFilterValueInput = ({
  value,
  onChange,
  disabled,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label: string;
}) => {

  const options = [
    { id: ASSET_ID_NOT_OWN, labelKey: 'resources.process-templates.text.asset_id_not_own', fallback: 'Not own asset id' },
    { id: ASSET_ID_OWN, labelKey: 'resources.process-templates.text.asset_id_own', fallback: 'Own asset id' },
  ];
  const translateLabel = useTranslateLabel();
  const selected = [ASSET_ID_NOT_OWN, ASSET_ID_OWN].includes(value) ? value : ASSET_ID_NOT_OWN;

  return (
    <FormControl size="small" fullWidth disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={selected}
        label={label}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {translateLabel(option.labelKey, option.fallback)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

};

const AvailableFilterValueInput = ({
  value,
  onChange,
  disabled,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label: string;
}) => {

  const options = [
    { id: AVAILABLE_YES, labelKey: 'resources.process-templates.text.filter_available_yes', fallback: 'Available' },
    { id: AVAILABLE_NO, labelKey: 'resources.process-templates.text.filter_available_no', fallback: 'Not available' },
  ];
  const translateLabel = useTranslateLabel();
  const selected = [AVAILABLE_YES, AVAILABLE_NO].includes(value) ? value : AVAILABLE_YES;

  return (
    <FormControl size="small" fullWidth disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={selected}
        label={label}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {translateLabel(option.labelKey, option.fallback)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

};

const getAnyOwnGroupsValueForComparator = (comparator: string): string | null => {

  if (comparator === 'not-equals' || comparator === 'not-contains') return ANY_OWN_GROUPS_NOT_EQUALS;
  if (comparator === 'equals' || comparator === 'contains') return ANY_OWN_GROUPS_EQUALS;
  return null;

};

const isAnyOwnGroupsValue = (value: string): boolean => (
  [ANY_OWN_GROUPS_EQUALS, ANY_OWN_GROUPS_NOT_EQUALS].includes(value)
);

const normalizeAnyOwnGroupsFilters = (filters: Record<string, string>): Record<string, string> => {

  const legacyValue = filters.groups;
  if (!isAnyOwnGroupsValue(legacyValue || '')) return filters;

  const normalizedKey = legacyValue === ANY_OWN_GROUPS_NOT_EQUALS ? 'groups:not-contains' : 'groups:contains';
  if (filters[normalizedKey] !== undefined) return filters;

  const next = { ...filters, [normalizedKey]: legacyValue };
  delete next.groups;
  return next;

};

const COMPARATOR_LABELS: Record<ResultOperator, { key: string; fallback: string }> = {
  '==': { key: 'resources.process-templates.text.comparator_equals', fallback: 'equals' },
  '!=': { key: 'resources.process-templates.text.comparator_not_equals', fallback: 'not equals' },
  '<': { key: 'resources.process-templates.text.comparator_less_than', fallback: 'less than' },
  '<=': { key: 'resources.process-templates.text.comparator_less_than_or_equal', fallback: 'less than or equal' },
  '>': { key: 'resources.process-templates.text.comparator_greater_than', fallback: 'greater than' },
  '>=': { key: 'resources.process-templates.text.comparator_greater_than_or_equal', fallback: 'greater than or equal' },
};

const DistanceFilterValueInput = ({
  value,
  onChange,
  disabled,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label: string;
}) => {

  const resolvedValue = value || getDefaultResultFilterValue('distance') || '';
  const { operator, operand } = parseOperatorValue(resolvedValue);

  const updateOperand = (nextOperand: string) => {

    onChange(formatOperatorValue(operator, nextOperand));

  };

  return (
    <NumericTextField
      size="small"
      fullWidth
      value={operand}
      disabled={disabled}
      onChange={updateOperand}
      label={label}
      inputProps={{ min: 0 }}
    />
  );

};

const EmbeddedTimestampFilterValueInput = ({
  value,
  onChange,
  disabled,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label: string;
}) => {

  const displaySeconds = embeddedTimestampToDisplaySeconds(value);

  return (
    <NumericTextField
      size="small"
      fullWidth
      value={displaySeconds}
      disabled={disabled}
      onChange={(nextValue) => onChange(embeddedTimestampToStoredMilliseconds(nextValue))}
      label={label}
      inputProps={{ min: 0 }}
    />
  );

};

const FilterValueInput = ({
  filterKey,
  value,
  onChange,
  disabled,
}: {
  filterKey: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) => {

  const translate = useTranslate();
  const translateLabel = useTranslateLabel();
  const inputType = getFilterValueInputType(filterKey);
  const isResultFilter = isResultOnlyFilterKey(filterKey);

  if (!isResultFilter && inputType === 'groups') {

    const parsed = parseCompoundFilterKey(filterKey);
    let anyOwnGroupsValue = parsed ? getAnyOwnGroupsValueForComparator(parsed.comparator) : null;
    if (isAnyOwnGroupsValue(value)) {

      anyOwnGroupsValue = value;

    }
    const extraOptions = anyOwnGroupsValue
      ? [{
        id: anyOwnGroupsValue,
        name: translateLabel('resources.process-templates.text.any_own_groups', 'Any own groups'),
      }]
      : [];

    return (
      <ReferenceValueInput
        reference="asset-groups"
        value={value}
        onChange={onChange}
        disabled={disabled}
        label={translate('resources.customer-links.fields.groups', { _: 'Group' })}
        extraOptions={extraOptions}
      />
    );

  }

  if (!isResultFilter && inputType === 'insideZones') {

    return (
      <ReferenceValueInput
        reference="zones"
        value={value}
        onChange={onChange}
        disabled={disabled}
        label={translate('resources.zones.text.title_single', { _: 'Zone' })}
      />
    );

  }

  if (!isResultFilter && inputType === 'id') {

    return (
      <ReferenceValueInput
        reference="all-assets"
        value={value}
        onChange={onChange}
        disabled={disabled}
        label={translate('resources.assets.text.single_name')}
      />
    );

  }

  if (!isResultFilter && inputType === 'embeddedTimestamp') {

    return (
      <EmbeddedTimestampFilterValueInput
        value={value}
        onChange={onChange}
        disabled={disabled}
        label={translateLabel('resources.process-templates.fields.filter_value_seconds', 'Seconds')}
      />
    );

  }

  if (filterKey === 'customerId') {

    return (
      <ReferenceValueInput
        reference="customers"
        value={value}
        onChange={onChange}
        disabled={disabled}
        label={translate('resources.customers.text.title_single', { _: 'Customer' })}
      />
    );

  }

  if (filterKey === 'assetId') {

    return (
      <AssetIdFilterValueInput
        value={value}
        onChange={onChange}
        disabled={disabled}
        label={translateLabel('resources.process-templates.fields.filter_value', 'Value')}
      />
    );

  }

  if (filterKey === 'available') {

    return (
      <AvailableFilterValueInput
        value={value}
        onChange={onChange}
        disabled={disabled}
        label={translateLabel('resources.process-templates.fields.filter_value', 'Value')}
      />
    );

  }

  if (filterKey === 'contacted') {

    return (
      <ContactedFilterValueInput
        label={translateLabel('resources.process-templates.fields.filter_value', 'Value')}
      />
    );

  }

  if (filterKey === 'distance') {

    return (
      <DistanceFilterValueInput
        value={value}
        onChange={onChange}
        disabled={disabled}
        label={translateLabel('resources.process-templates.fields.filter_value', 'Value')}
      />
    );

  }

  return (
    <TextField
      size="small"
      fullWidth
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      label={translate('resources.process-templates.fields.filter_value', { _: 'Value' })}
      placeholder={isResultFilter ? 'e.g. ==true, <750' : undefined}
    />
  );

};

const DisabledComparatorInput = () => {

  const translateLabel = useTranslateLabel();

  return (
    <TextField
      size="small"
      fullWidth
      value=""
      disabled
      label={translateLabel('resources.process-templates.text.comparator_equals', 'equals')}
      InputProps={{ readOnly: true }}
    />
  );

};

const FilterComparatorInput = ({
  filterKey,
  filterValue,
  presetOptions,
  disabled,
  onCompoundComparatorChange,
  onDistanceOperatorChange,
}: {
  filterKey: string;
  filterValue: string;
  presetOptions: FilterOption[];
  disabled?: boolean;
  onCompoundComparatorChange: (comparator: string) => void;
  onDistanceOperatorChange: (operator: ResultOperator) => void;
}) => {

  const translateLabel = useTranslateLabel();

  if (isNewFilterKey(filterKey)) {

    return <DisabledComparatorInput />;

  }

  if (filterKey === 'distance') {

    const resolvedValue = filterValue || getDefaultResultFilterValue('distance') || '';
    const { operator } = parseOperatorValue(resolvedValue);
    const comparatorOptions = getComparatorOptions();

    return (
      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel>{translateLabel('resources.process-templates.fields.filter_comparator', 'Comparator')}</InputLabel>
        <Select
          value={operator}
          label={translateLabel('resources.process-templates.fields.filter_comparator', 'Comparator')}
          onChange={(event) => onDistanceOperatorChange(event.target.value as ResultOperator)}
        >
          {comparatorOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {translateLabel(COMPARATOR_LABELS[option].key, COMPARATOR_LABELS[option].fallback)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );

  }

  const parsed = parseCompoundFilterKey(filterKey);
  if (parsed) {

    const comparators = getComparatorsForFamily(parsed.family, presetOptions);

    return (
      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel>{translateLabel('resources.process-templates.fields.filter_comparator', 'Comparator')}</InputLabel>
        <Select
          value={parsed.comparator}
          label={translateLabel('resources.process-templates.fields.filter_comparator', 'Comparator')}
          onChange={(event) => onCompoundComparatorChange(event.target.value)}
        >
          {comparators.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );

  }

  return <DisabledComparatorInput />;

};

const FilterKeyInput = ({
  filterKey,
  options,
  usedFamilies,
  presetOptions,
  disabled,
  onChange,
  onClear,
}: {
  filterKey: string;
  options: FilterOption[];
  usedFamilies: string[];
  presetOptions: FilterOption[];
  disabled?: boolean;
  onChange: (newKey: string) => void;
  onClear: () => void;
}) => {

  const translate = useTranslate();
  const displayFamily = isNewFilterKey(filterKey) ? '' : getFilterFamily(filterKey);
  const selected = React.useMemo(() => (
    options.find((option) => option.value === displayFamily)
    || (displayFamily ? { value: displayFamily, label: displayFamily } : null)
  ), [displayFamily, options]);
  const [inputValue, setInputValue] = React.useState(selected?.label || displayFamily);

  React.useEffect(() => {

    setInputValue(selected?.label || displayFamily);

  }, [displayFamily, selected]);

  const commitKey = (rawKey: string) => {

    const trimmedKey = rawKey.trim();
    const matchingOption = options.find((option) => option.value === trimmedKey || option.label === trimmedKey);
    const optionValue = matchingOption?.value || trimmedKey;
    const nextKey = resolveFilterKey(optionValue, presetOptions);
    if (!nextKey) {

      if (!isNewFilterKey(filterKey)) {

        onClear();

      }
      return;

    }
    if (getFilterFamily(nextKey) === displayFamily) return;
    onChange(nextKey);

  };

  const handleClear = () => {

    setInputValue('');
    if (!isNewFilterKey(filterKey)) {

      onClear();

    }

  };

  return (
    <Autocomplete<FilterOption, false, false, true>
      freeSolo
      options={options}
      value={selected}
      inputValue={inputValue}
      disabled={disabled}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
      isOptionEqualToValue={(option, val) => option.value === (typeof val === 'string' ? val : val.value)}
      getOptionDisabled={(option) => option.value !== displayFamily && usedFamilies.includes(option.value)}
      onChange={(_, newValue) => {

        if (newValue === null) {

          handleClear();
          return;

        }
        if (typeof newValue === 'string') {

          setInputValue(newValue);
          commitKey(newValue);
          return;

        }
        if (newValue?.value) {

          setInputValue(newValue.label);
          commitKey(newValue.value);

        }

      }}
      onInputChange={(_, newInputValue, reason) => {

        if (reason === 'clear') {

          handleClear();
          return;

        }
        if (reason === 'input') {

          setInputValue(newInputValue);

        }

      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          fullWidth
          label={translate('resources.process-templates.fields.filter_key', { _: 'Filter' })}
          placeholder={translate('resources.process-templates.text.custom_filter_key', { _: 'Select or type a filter key' })}
          onBlur={() => commitKey(inputValue)}
        />
      )}
      fullWidth
    />
  );

};

export const XgacFilterEditor = ({
  value,
  onChange,
  filterField,
  disabled,
}: XgacFilterEditorProps) => {

  const label = useTranslateLabel();
  const translate = useTranslate();
  const [draftFilters, setDraftFilters] = React.useState<Record<string, string>>({});
  const [filterOrder, setFilterOrder] = React.useState<string[]>([]);
  const [expanded, setExpanded] = React.useState(
    () => Object.keys(flattenXgacFilter(value || {})).length > 0,
  );
  const presetOptions = [
    ...(filterField?.input?.meta?.requestOptions || []),
    ...(filterField?.input?.meta?.resultOptions || []),
  ];
  const currentValue = React.useMemo(() => value || {}, [value]);
  const persistedFilters = normalizeAnyOwnGroupsFilters(flattenXgacFilter(currentValue));
  const flatFilters = { ...persistedFilters, ...draftFilters };

  React.useEffect(() => {

    setFilterOrder((prev) => {

      const persistedKeys = Object.keys(persistedFilters);
      const draftKeys = Object.keys(draftFilters);
      const allKeys = new Set([...persistedKeys, ...draftKeys]);

      if (prev.length === 0) {

        return [...persistedKeys, ...draftKeys];

      }

      const retained = prev.filter((key) => allKeys.has(key));
      const added = [...persistedKeys, ...draftKeys].filter((key) => !retained.includes(key));
      return added.length > 0 ? [...retained, ...added] : retained;

    });

  }, [persistedFilters, draftFilters]);

  const displayKeys = filterOrder.filter((key) => key in flatFilters);
  const usedKeys = displayKeys.length > 0 ? displayKeys : Object.keys(flatFilters);
  const usedFamilies = usedKeys
    .filter((key) => !isNewFilterKey(key))
    .map((key) => getFilterFamily(key));
  const activeFilterCount = usedKeys.filter((key) => !isNewFilterKey(key)).length;
  const selectorOptions = getSelectorFilterOptions(presetOptions);
  const options = mergeFilterOptions(presetOptions, flatFilters);
  const canAddFilter = usedKeys.length < selectorOptions.length;
  const hasDistanceFilter = flatFilters.distance !== undefined;

  const appendOrderKey = (key: string) => {

    setFilterOrder((prev) => (prev.includes(key) ? prev : [...prev, key]));

  };

  const removeOrderKey = (key: string) => {

    setFilterOrder((prev) => prev.filter((orderKey) => orderKey !== key));

  };

  const renameOrderKey = (oldKey: string, newKey: string) => {

    setFilterOrder((prev) => prev.map((orderKey) => (orderKey === oldKey ? newKey : orderKey)));

  };

  const updatePersistedFilters = (nextPersisted: Record<string, string>) => {

    const normalized = nextPersisted.contacted !== undefined
      ? { ...nextPersisted, contacted: CONTACTED_NOT_BEFORE }
      : nextPersisted;
    onChange(splitXgacFilter(normalized, currentValue.max));

  };

  const contactedValue = currentValue.result?.contacted;
  React.useEffect(() => {

    if (contactedValue !== undefined && contactedValue !== CONTACTED_NOT_BEFORE) {

      const flat = flattenXgacFilter(currentValue);
      onChange(splitXgacFilter({ ...flat, contacted: CONTACTED_NOT_BEFORE }, currentValue.max));

    }

  }, [contactedValue, currentValue, onChange]);

  const updateKey = (oldKey: string, rawKey: string) => {

    const newKey = resolveFilterKey(rawKey, presetOptions);
    if (!newKey || newKey === oldKey) return;

    const newFamily = getFilterFamily(newKey);
    const hasDuplicateFamily = usedKeys.some(
      (key) => key !== oldKey && !isNewFilterKey(key) && getFilterFamily(key) === newFamily,
    );
    if (hasDuplicateFamily) return;
    if (flatFilters[newKey] !== undefined && newKey !== oldKey) return;

    const oldValue = flatFilters[oldKey] ?? '';
    let nextValue = oldValue;
    if (newKey === 'contacted') {

      nextValue = CONTACTED_NOT_BEFORE;

    } else if (!nextValue) {

      nextValue = getDefaultResultFilterValue(newKey) ?? nextValue;

    }

    if (isNewFilterKey(oldKey)) {

      setDraftFilters((prev) => {

        const next = { ...prev };
        delete next[oldKey];
        return next;

      });
      renameOrderKey(oldKey, newKey);
      updatePersistedFilters({ ...persistedFilters, [newKey]: nextValue });
      return;

    }

    const nextPersisted = { ...persistedFilters };
    nextPersisted[newKey] = nextValue;
    delete nextPersisted[oldKey];
    renameOrderKey(oldKey, newKey);
    updatePersistedFilters(nextPersisted);

  };

  const resetFilterKey = (oldKey: string) => {

    if (isNewFilterKey(oldKey)) {

      setDraftFilters((prev) => ({ ...prev, [oldKey]: '' }));
      return;

    }

    const nextPersisted = { ...persistedFilters };
    delete nextPersisted[oldKey];
    updatePersistedFilters(nextPersisted);

    const newKey = `${NEW_FILTER_KEY_PREFIX}${uuidv4()}`;
    renameOrderKey(oldKey, newKey);
    setDraftFilters((prev) => ({ ...prev, [newKey]: '' }));

  };

  const updateCompoundComparator = (oldKey: string, comparator: string) => {

    const parsed = parseCompoundFilterKey(oldKey);
    if (!parsed) return;

    const newKey = buildCompoundFilterKey(parsed.family, comparator);
    if (newKey === oldKey) return;

    const oldValue = flatFilters[oldKey] ?? '';
    const nextValue = parsed.family === 'groups' && isAnyOwnGroupsValue(oldValue)
      ? getAnyOwnGroupsValueForComparator(comparator) ?? oldValue
      : oldValue;
    const nextPersisted = { ...persistedFilters };
    nextPersisted[newKey] = nextValue;
    delete nextPersisted[oldKey];
    renameOrderKey(oldKey, newKey);
    updatePersistedFilters(nextPersisted);

  };

  const updateValue = (key: string, newValue: string) => {

    if (key === 'contacted') return;
    if (isNewFilterKey(key)) {

      setDraftFilters((prev) => ({ ...prev, [key]: newValue }));
      return;

    }
    updatePersistedFilters({ ...persistedFilters, [key]: newValue });

  };

  const updateDistanceOperator = (key: string, nextOperator: ResultOperator) => {

    const current = flatFilters[key] || getDefaultResultFilterValue('distance') || '';
    const { operand } = parseOperatorValue(current);
    updateValue(key, formatOperatorValue(nextOperator, operand));

  };

  const removeFilter = (key: string) => {

    removeOrderKey(key);
    if (isNewFilterKey(key)) {

      setDraftFilters((prev) => {

        const next = { ...prev };
        delete next[key];
        return next;

      });
      return;

    }

    const nextPersisted = { ...persistedFilters };
    delete nextPersisted[key];
    updatePersistedFilters(nextPersisted);

  };

  const addFilter = () => {

    if (!canAddFilter) return;

    const newKey = `${NEW_FILTER_KEY_PREFIX}${uuidv4()}`;
    appendOrderKey(newKey);
    setDraftFilters((prev) => ({ ...prev, [newKey]: '' }));
    setExpanded(true);

  };

  const maxResultsHelper = (() => {

    if ((currentValue.max ?? 0) === 0) {

      return (
        <FormHelperText>{label('resources.process-templates.text.max_unlimited', 'Unlimited')}</FormHelperText>
      );

    }

    if ((currentValue.max ?? 0) > 10 && hasDistanceFilter) {

      return (
        <FormHelperText error>
          {label(
            'resources.process-templates.text.max_distance_warning',
            'Warning: do not select more than 10 items when using the distance filter. '
            + 'Review the simulation result before proceeding.',
          )}
        </FormHelperText>
      );

    }

    return null;

  })();

  return (
    <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'divider' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: expanded ? 1 : 0,
          cursor: 'pointer',
        }}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <IconButton
          size="small"
          aria-label={expanded
            ? translate('ra.action.close', { _: 'Collapse' })
            : translate('ra.action.expand', { _: 'Expand' })}
          onClick={(event) => {

            event.stopPropagation();
            setExpanded((prev) => !prev);

          }}
        >
          {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
        <Typography variant="subtitle1" sx={{ flex: 1 }}>
          {translate('resources.process-templates.text.xgac_filter')}
          {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
        </Typography>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, mb: 1,
          }}>
            <Typography variant="subtitle2">
              {translate('resources.process-templates.text.filters', { _: 'Filters' })} ({usedKeys.length})
            </Typography>
            <IconButton
              size="small"
              color="success"
              disabled={disabled || !canAddFilter}
              onClick={addFilter}
              aria-label={translate('resources.process-templates.text.add_filter', { _: 'Add filter' })}
            >
              <AddCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Box>

          {usedKeys.map((key) => {

            const filterValue = flatFilters[key] || '';
            const isInvalidKey = key.length > 0 && !isNewFilterKey(key) && !isValidFilterKey(key);
            const isInvalidValue = isResultOnlyFilterKey(key)
            && filterValue
            && key !== 'customerId'
            && key !== 'contacted'
            && key !== 'assetId'
            && key !== 'available'
            && key !== 'distance'
            && !RESULT_FILTER_PATTERN.test(filterValue);

            return (
              <Box key={key} sx={FILTER_ROW_SX}>
                <Box sx={FILTER_COLUMN_SX}>
                  <FilterKeyInput
                    filterKey={key}
                    options={options}
                    usedFamilies={usedFamilies}
                    presetOptions={presetOptions}
                    disabled={disabled}
                    onChange={(newKey) => updateKey(key, newKey)}
                    onClear={() => resetFilterKey(key)}
                  />
                </Box>
                <Box sx={FILTER_COLUMN_SX}>
                  <FilterComparatorInput
                    filterKey={key}
                    filterValue={filterValue}
                    presetOptions={presetOptions}
                    disabled={disabled || isNewFilterKey(key)}
                    onCompoundComparatorChange={(comparator) => updateCompoundComparator(key, comparator)}
                    onDistanceOperatorChange={(operator) => updateDistanceOperator(key, operator)}
                  />
                </Box>
                <Box sx={FILTER_COLUMN_SX}>
                  <FilterValueInput
                    filterKey={key}
                    value={filterValue}
                    disabled={disabled || !key || isNewFilterKey(key)}
                    onChange={(newValue) => updateValue(key, newValue)}
                  />
                </Box>
                <IconButton
                  size="small"
                  color="error"
                  disabled={disabled}
                  onClick={() => removeFilter(key)}
                  aria-label={translate('ra.action.delete')}
                  sx={{ mt: 0.5 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
                {isInvalidKey && (
                  <FormHelperText error sx={{ gridColumn: '1 / -1' }}>
                    {translate('resources.process-templates.text.invalid_filter_key', { _: 'This filter key is not supported by ATS getAssets' })}
                  </FormHelperText>
                )}
                {isInvalidValue && (
                  <FormHelperText error sx={{ gridColumn: '1 / -1' }}>
                    {translate('resources.process-templates.text.invalid_filter_value', { _: 'Invalid filter value' })}
                  </FormHelperText>
                )}
              </Box>
            );

          })}
        </Box>

        <NumericInputField
          size="small"
          variant="outlined"
          label={label('resources.process-templates.text.max_results', 'Max. results (0 for unlimited)')}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: 0 }}
          value={currentValue.max ?? 0}
          disabled={disabled}
          onChange={(max) => onChange({
            ...currentValue,
            max,
          })}
          sx={{ width: '100%', maxWidth: 360, mt: 1 }}
        />
        {maxResultsHelper}
      </Collapse>
    </Box>
  );

};
